// FaqPage — section tabs (persisted to localStorage) + per-section accordion
// (single item open, first item open on section switch). Keyboard Enter/Space + aria-expanded.
// Content from src/data/faq.tsx. Rich answers are JSX (Phase 31 — no dangerouslySetInnerHTML).
import { useState } from 'react';
import { faqSectionOrder, faqSections } from '@/data/faq';
import Tabs from '@/components/ui/Tabs/Tabs';
import '@/styles/pages/faq.css';

const STORAGE_KEY = 'quralyst:faqActiveSection';
const PANEL_ID = 'faq-section-panel';

function toTabId(section: string): string {
  return section.replace(/\s+/g, '-');
}

function fromTabId(id: string): string {
  return faqSectionOrder.find((section) => toTabId(section) === id) ?? faqSectionOrder[0];
}

const FAQ_TABS = faqSectionOrder.map((section) => ({
  id: toTabId(section),
  label: section,
  controls: PANEL_ID,
}));

export default function FaqPage() {
  const [activeSection, setActiveSection] = useState<string>(() => {
    const saved = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
    return saved && faqSections[saved] ? saved : faqSectionOrder[0];
  });
  // Index of the open item within the active section (0 = first open by default).
  const [openIndex, setOpenIndex] = useState(0);

  const selectSection = (section: string) => {
    setActiveSection(section);
    setOpenIndex(0); // open first item of the newly selected section
    try {
      window.localStorage.setItem(STORAGE_KEY, section);
    } catch {
      /* ignore */
    }
  };

  const toggleItem = (index: number) => {
    setOpenIndex((cur) => (cur === index ? -1 : index));
  };

  const items = faqSections[activeSection] ?? [];

  return (
    <div className="faq-container">
      <header className="faq-header">
        <h1 className="faq-title">Frequently Asked Questions</h1>
        <p className="faq-subtitle">Your finance Co pilot</p>
      </header>

      <Tabs
        ariaLabel="FAQ sections"
        className="quralyst-tabs--flush"
        tabs={FAQ_TABS}
        active={toTabId(activeSection)}
        onChange={(id) => selectSection(fromTabId(id))}
      />

      <div
        className="faq-section-panel"
        id={PANEL_ID}
        role="tabpanel"
        aria-labelledby={`tab-${toTabId(activeSection)}`}
      >
        <div className="faq-accordion">
          {items.map((faq, i) => {
            const open = i === openIndex;
            const answerId = `faq-answer-${i}`;
            return (
              <div key={i} className={`faq-item${open ? ' active' : ''}`}>
                <button
                  type="button"
                  className="faq-question btn-unstyled"
                  aria-expanded={open}
                  aria-controls={answerId}
                  onClick={() => toggleItem(i)}
                >
                  {/* a11y (Phase 35): h2, not h3 — the page heading is the h1 and questions are
                      its direct sub-sections (axe: heading-order). .faq-question-text overrides
                      every element-level style (size/weight/color/margin) so it renders identically. */}
                  <h2 className="faq-question-text">{faq.question}</h2>
                  <i className="bi bi-chevron-down faq-icon" aria-hidden="true" />
                </button>
                <div className="faq-answer" id={answerId}>
                  {typeof faq.answer === 'string' ? (
                    <p className="faq-answer-text">{faq.answer}</p>
                  ) : (
                    <div className="faq-answer-text faq-answer-rich">{faq.answer}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
